import React, { useEffect, useRef, useState } from 'react'
import { Alert, Skeleton } from '@pankod/refine-antd'

import type { PDFDocumentLoadingTask, RenderTask } from 'pdfjs-dist'

type TPdfCanvasPreviewProps = {
  url: string
  page?: number
  className?: string
  onLoad?: (pageCount: number) => void
  onError?: () => void
  onClick?: () => void
}

export const PdfCanvasPreview: React.FC<TPdfCanvasPreviewProps> = ({
  url,
  page = 1,
  className,
  onLoad,
  onError,
  onClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const onLoadRef = useRef(onLoad)
  const onErrorRef = useRef(onError)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    onLoadRef.current = onLoad
    onErrorRef.current = onError
  }, [onError, onLoad])

  useEffect(() => {
    let disposed = false
    let resizeObserver: ResizeObserver | undefined
    let renderTask: RenderTask | undefined
    let loadingTask: PDFDocumentLoadingTask | undefined

    const handleError = (renderError: unknown) => {
      if (
        disposed ||
        (renderError instanceof Error &&
          renderError.name === 'RenderingCancelledException')
      ) {
        return
      }

      setLoading(false)
      setError(true)
      onErrorRef.current?.()
    }

    const loadPdf = async () => {
      setLoading(true)
      setError(false)

      try {
        const pdfjs = await import('pdfjs-dist')
        if (!pdfjs.GlobalWorkerOptions.workerSrc) {
          pdfjs.GlobalWorkerOptions.workerSrc = `/api/pdfjs-worker?v=${pdfjs.version}`
        }

        const task = pdfjs.getDocument({ url })
        loadingTask = task
        const pdfDocument = await task.promise
        if (!disposed) {
          onLoadRef.current?.(pdfDocument.numPages)
        }
        const pdfPage = await pdfDocument.getPage(page)

        const renderPage = async () => {
          const container = containerRef.current
          const canvas = canvasRef.current
          if (!container || !canvas || disposed) return

          renderTask?.cancel()

          const baseViewport = pdfPage.getViewport({ scale: 1 })
          const availableWidth = container.clientWidth || baseViewport.width
          const cssScale = availableWidth / baseViewport.width
          const pixelRatio = window.devicePixelRatio || 1
          const viewport = pdfPage.getViewport({
            scale: cssScale * pixelRatio,
          })
          const canvasContext = canvas.getContext('2d')
          if (!canvasContext) {
            throw new Error('Canvas context is not available')
          }

          canvas.width = viewport.width
          canvas.height = viewport.height
          canvas.style.width = `${viewport.width / pixelRatio}px`
          canvas.style.height = `${viewport.height / pixelRatio}px`

          renderTask = pdfPage.render({
            canvas,
            canvasContext,
            viewport,
          })
          await renderTask.promise

          if (!disposed) {
            setLoading(false)
          }
        }

        await renderPage()

        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => {
            void renderPage().catch(handleError)
          })
          if (containerRef.current) {
            resizeObserver.observe(containerRef.current)
          }
        }
      } catch (loadError) {
        handleError(loadError)
      }
    }

    void loadPdf()

    return () => {
      disposed = true
      resizeObserver?.disconnect()
      renderTask?.cancel()
      void loadingTask?.destroy()
    }
  }, [page, url])

  if (error) {
    return (
      <Alert
        type="error"
        message="Gagal memuat preview PDF"
        showIcon
        className={className}
      />
    )
  }

  return (
    <div
      ref={containerRef}
      className={className}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (onClick && (event.key === 'Enter' || event.key === ' ')) {
          onClick()
        }
      }}
    >
      {loading && (
        <div className="flex justify-center p-4">
          <Skeleton.Image active className="!w-[200px] !h-[282px]" />
        </div>
      )}
      <canvas ref={canvasRef} className={loading ? 'hidden' : 'block'} />
    </div>
  )
}
